import { z } from "zod";

// Event display time (2026-07-23 operator decision, final form): the DB stores the
// true instant + the venue's IANA zone; the API resolves them into venue-local
// wall-clock COMPONENTS once, server-side, and clients render them literally — no
// timezone conversion, no precision flags, nothing to get wrong on a frontend. A
// Paris concert ships `{date: "2026-08-01", time: "20:00"}` and every viewer reads
// the France time. `time: null` = the source only published a date.
export const eventStartLocalSchema = z.object({
  /** venue-local calendar date, `YYYY-MM-DD` */
  date: z.string(),
  /** venue-local 24h clock, `HH:MM`; null = date-only (render no time) */
  time: z.string().nullable(),
});
export type EventStartLocal = z.infer<typeof eventStartLocalSchema>;

/** Resolve a stored event instant into venue-local wall-clock components.
 *  `timezone` null = the zone is unknown and the stored value IS the wall clock
 *  stamped as UTC (Soundcharts outside the single-zone country map, legacy imports)
 *  — formatting in UTC reads it back verbatim. Date-only rows (`timeKnown` false)
 *  always resolve the date in UTC (they store the venue date's UTC midnight). */
export function toEventStartLocal(
  value: string | Date,
  timezone: string | null | undefined,
  timeKnown: boolean,
): EventStartLocal {
  const instant = value instanceof Date ? value : new Date(value);
  const zone = timeKnown ? (timezone ?? "UTC") : "UTC";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(instant);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    // Intl renders midnight as "24:00" under hourCycle h24 quirks in some runtimes —
    // normalize to "00".
    time: timeKnown ? `${get("hour").replace(/^24$/, "00")}:${get("minute")}` : null,
  };
}

// The rule lives in platform-wire so Link in Bio's My Tickets and the app
// label a vendor's release time the same way.
export { formatReleaseTime } from "@libs/platform-wire";
